import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  AlertCircle,
  CheckCircle,
  Clock,
  Eye,
  FileCheck,
  MoreHorizontal,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type {
  GetDocumentReview,
  ListDocumentReviews,
} from '@cold-ambulance/client';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  ScrollArea,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Textarea,
} from '@cold-ambulance/shadcn';
import {
  TableRowSort,
  ViewStoredFile,
  client,
  formatDate,
  useAction,
  useData,
} from '@cold-ambulance/ui';

import { createdAtColumn } from '../../components/columns.tsx';
import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';
import {
  StickyTableBox,
  StickyTableHeader,
} from '../../components/sticky-table-box.tsx';

function DocumentStatusBadges({
  approvedCount,
  pendingCount,
  rejectedCount,
}: {
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {approvedCount > 0 && (
        <Badge variant="outline" className="gap-1">
          <CheckCircle className="h-3 w-3" />
          {approvedCount} approved
        </Badge>
      )}
      {pendingCount > 0 && (
        <Badge variant="secondary" className="gap-1">
          <Clock className="h-3 w-3" />
          {pendingCount} pending
        </Badge>
      )}
      {rejectedCount > 0 && (
        <Badge variant="destructive" className="gap-1">
          <XCircle className="h-3 w-3" />
          {rejectedCount} rejected
        </Badge>
      )}
    </div>
  );
}

function OfferDetailsCard({
  data,
  approvedCount,
  pendingCount,
  rejectedCount,
}: {
  data: GetDocumentReview;
  approvedCount: number;
  pendingCount: number;
  rejectedCount: number;
}) {
  const totalDocs = data.documents.length;

  return (
    <Card>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-muted-foreground text-xs">Vendor</p>
            <p className="font-medium">{data.vendor.name}</p>
            <p className="text-muted-foreground text-xs">{data.vendor.email}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Project</p>
            <p className="font-medium">{data.request.projectName}</p>
            <p className="text-muted-foreground text-xs">
              {formatDate(data.request.scheduleStartDate)} -{' '}
              {formatDate(data.request.scheduleEndDate)}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Price</p>
            <p className="font-medium">{data.price.toLocaleString()} SAR</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Progress</p>
            <p className="font-medium">
              {approvedCount}/{totalDocs} approved
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Offer ID</p>
            <p className="text-muted-foreground font-mono text-xs">
              {data.id.slice(0, 8)}...
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Created</p>
            <p className="font-medium">{formatDate(data.createdAt)}</p>
          </div>
        </div>
        <DocumentStatusBadges
          approvedCount={approvedCount}
          pendingCount={pendingCount}
          rejectedCount={rejectedCount}
        />
      </CardContent>
    </Card>
  );
}

export default function DocumentReviewsPage() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns: ColumnDef<ListDocumentReviews['records'][number]>[] = [
    {
      accessorKey: 'vendor.name',
      header: 'Vendor',
      cell: ({ row }) => (
        <span className="font-medium">{row.original.vendor.name}</span>
      ),
    },
    {
      accessorKey: 'request.projectName',
      header: 'Project',
      cell: ({ row }) => (
        <span className="max-w-[200px] truncate">
          {row.original.request.projectName}
        </span>
      ),
    },
    {
      accessorKey: 'price',
      header: 'Price',
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.price.toLocaleString()} SAR
        </span>
      ),
    },
    {
      id: 'documents',
      header: 'Documents',
      enableSorting: false,
      cell: ({ row }) => {
        const uploaded = row.original.documents.length;
        const required = row.original.request.requiredDocuments.length;
        return (
          <Badge variant="secondary">
            {uploaded}/{required} uploaded
          </Badge>
        );
      },
    },
    {
      id: 'rejectionStatus',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => {
        const { approvedCount, rejectedCount, pendingCount } = row.original;
        return (
          <DocumentStatusBadges
            approvedCount={approvedCount}
            pendingCount={pendingCount}
            rejectedCount={rejectedCount}
          />
        );
      },
    },
    createdAtColumn(),
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const offer = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <ReviewDocumentsDialog
                offerId={offer.id}
                onAction={() => dataQuery.refetch()}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                    <Eye className="mr-2 h-4 w-4" />
                    {offer.pendingCount > 0
                      ? 'Review Documents'
                      : 'View Documents'}
                  </DropdownMenuItem>
                }
              />
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const dataQuery = useData(
    'GET /admin/document-reviews',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy:
        sorting.length > 0
          ? (sorting[0].id as 'createdAt' | 'updatedAt')
          : undefined,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined,
    },
    { placeholderData: keepPreviousData },
  );

  const table = useReactTable({
    data: dataQuery.data?.records || [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <CountablePageHeader
        title="Document Reviews"
        totalCount={`${dataQuery.data?.totalCount || 0} total reviews`}
        icon={FileCheck}
      />

      <StickyTableBox>
        <Table>
          <StickyTableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </StickyTableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No pending document reviews.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </StickyTableBox>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />
    </div>
  );
}

function ReviewDocumentsDialog({
  offerId,
  onAction,
  trigger,
}: {
  offerId: string;
  onAction: () => void;
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingDoc, setRejectingDoc] = useState<{
    id: string;
    label: string;
  } | null>(null);

  const detailQuery = useQuery({
    queryKey: ['document-review', offerId],
    queryFn: () =>
      client.request('GET /admin/document-reviews/{offerId}', { offerId }),
    enabled: open,
  });

  const data = detailQuery.data;

  const approvedDocs =
    data?.documents.filter((doc) => doc.status === 'APPROVED') || [];
  const pendingDocs =
    data?.documents.filter((doc) => doc.status === 'PENDING') || [];
  const rejectedDocs =
    data?.documents.filter((doc) => doc.status === 'REJECTED') || [];

  const handleRejectClick = (doc: { id: string; label: string }) => {
    setRejectingDoc(doc);
    setRejectDialogOpen(true);
  };

  const handleDocumentApproved = () => {
    detailQuery.refetch();
    onAction();
  };

  const handleRejectSuccess = () => {
    setRejectingDoc(null);
    detailQuery.refetch();
    onAction();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent className="max-w-3xl p-0">
          <DialogHeader className="px-6 pt-6">
            <DialogTitle>Review Documents</DialogTitle>
            <DialogDescription>
              Review and approve or reject each document
            </DialogDescription>
          </DialogHeader>

          {detailQuery.isLoading ? (
            <div className="flex h-60 items-center justify-center">
              <span className="text-muted-foreground">Loading...</span>
            </div>
          ) : data ? (
            <div className="space-y-4 px-6">
              <OfferDetailsCard
                data={data}
                approvedCount={approvedDocs.length}
                pendingCount={pendingDocs.length}
                rejectedCount={rejectedDocs.length}
              />

              <Separator />

              <DocumentsList
                rejectedDocs={rejectedDocs}
                pendingDocs={pendingDocs}
                approvedDocs={approvedDocs}
                onRejectClick={handleRejectClick}
                onDocumentApproved={handleDocumentApproved}
              />
            </div>
          ) : null}

          <DialogFooter className="px-6 pb-6">
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RejectDocumentDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        document={rejectingDoc}
        onSuccess={handleRejectSuccess}
      />
    </>
  );
}

function DocumentReviewCard({
  doc,
  onRejectClick,
  onApproveSuccess,
}: {
  doc: GetDocumentReview['documents'][number];
  onRejectClick: () => void;
  onApproveSuccess: () => void;
}) {
  const approveMutation = useAction(
    'PATCH /admin/offer-documents/{documentId}/approve',
    {
      meta: { toast: true },
      onSuccess: () => {
        onApproveSuccess();
        toast.success('Document approved');
      },
    },
  );

  const statusBadge = {
    APPROVED: (
      <Badge variant="outline" className="gap-1">
        <CheckCircle className="h-3 w-3" />
        Approved
      </Badge>
    ),
    REJECTED: (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" />
        Rejected
      </Badge>
    ),
    PENDING: (
      <Badge variant="secondary" className="gap-1">
        <Clock className="h-3 w-3" />
        Pending
      </Badge>
    ),
    NOT_SUBMITTED: (
      <Badge variant="outline" className="gap-1">
        <AlertCircle className="h-3 w-3" />
        Not submitted
      </Badge>
    ),
  } as const;

  return (
    <Card className="py-0">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <p className="font-medium">{doc.requirement.label}</p>
              {statusBadge[doc.status]}
            </div>
            <p className="text-muted-foreground text-sm">
              Uploaded {formatDate(doc.createdAt)}
            </p>

            {doc.status === 'REJECTED' && doc.rejectionReason && (
              <Alert variant="destructive" className="mt-3">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{doc.rejectionReason}</AlertDescription>
              </Alert>
            )}
          </div>

          <div className="flex items-center gap-2">
            <ViewStoredFile fileId={doc.fileId} />

            {doc.status !== 'APPROVED' && (
              <Button
                size="sm"
                variant="default"
                disabled={approveMutation.isPending}
                onClick={() => approveMutation.mutate({ documentId: doc.id })}
              >
                <CheckCircle className="me-1 h-4 w-4" />
                {approveMutation.isPending ? 'Approving...' : 'Approve'}
              </Button>
            )}

            {doc.status !== 'REJECTED' && doc.status !== 'APPROVED' && (
              <Button size="sm" variant="destructive" onClick={onRejectClick}>
                <XCircle className="me-1 h-4 w-4" />
                Reject
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function DocumentsList({
  rejectedDocs,
  pendingDocs,
  approvedDocs,
  onRejectClick,
  onDocumentApproved,
}: {
  rejectedDocs: GetDocumentReview['documents'];
  pendingDocs: GetDocumentReview['documents'];
  approvedDocs: GetDocumentReview['documents'];
  onRejectClick: (doc: { id: string; label: string }) => void;
  onDocumentApproved: () => void;
}) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium">Documents</h3>
      <ScrollArea className="max-h-72">
        <div className="space-y-3">
          {rejectedDocs.map((doc) => (
            <DocumentReviewCard
              key={doc.id}
              doc={doc}
              onRejectClick={() =>
                onRejectClick({ id: doc.id, label: doc.requirement.label })
              }
              onApproveSuccess={onDocumentApproved}
            />
          ))}

          {pendingDocs.map((doc) => (
            <DocumentReviewCard
              key={doc.id}
              doc={doc}
              onRejectClick={() =>
                onRejectClick({ id: doc.id, label: doc.requirement.label })
              }
              onApproveSuccess={onDocumentApproved}
            />
          ))}

          {approvedDocs.map((doc) => (
            <DocumentReviewCard
              key={doc.id}
              doc={doc}
              onRejectClick={() =>
                onRejectClick({ id: doc.id, label: doc.requirement.label })
              }
              onApproveSuccess={onDocumentApproved}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

function RejectDocumentDialog({
  open,
  onOpenChange,
  document,
  onSuccess,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: { id: string; label: string } | null;
  onSuccess: () => void;
}) {
  const [rejectionReason, setRejectionReason] = useState('');

  const rejectMutation = useAction(
    'PATCH /admin/offer-documents/{documentId}/reject',
    {
      meta: { toast: true },
      onSuccess: () => {
        onOpenChange(false);
        setRejectionReason('');
        onSuccess();
        toast.success('Document rejected');
      },
    },
  );

  const handleConfirmReject = () => {
    if (document && rejectionReason.trim()) {
      rejectMutation.mutate({
        documentId: document.id,
        reason: rejectionReason.trim(),
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertCircle className="text-destructive h-5 w-5" />
            Reject Document
          </AlertDialogTitle>
          <AlertDialogDescription>
            You are about to reject "{document?.label}". Please provide a reason
            for rejection.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4">
          <Textarea
            placeholder="Enter rejection reason..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            className="min-h-[100px]"
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setRejectionReason('')}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirmReject}
            disabled={!rejectionReason.trim() || rejectMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {rejectMutation.isPending ? 'Rejecting...' : 'Confirm Rejection'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
